// Shared pieces of the disOS reports: running header, deferred computation for the heavier reports.
import { useEffect, useState, type ReactNode } from 'react';
import { APP_VERSION, periodWord } from '../model';
import type { CaseView } from '../caseView';

/** `period` overrides the case's Monthly/Yearly label for reports whose figures are always annual (tax forms). */
export function ReportHead({ v, title, period }: { v: CaseView; title: string; period?: string }) {
  const d = new Date();
  return (
    <div className="br-top">
      <span><b>{title}</b> ({period ?? periodWord(v.c)})</span>
      <span>{v.c.info.caseNumber ? `${v.c.info.caseNumber} · ` : ''}{v.name}</span>
      <span>Printed {d.toLocaleDateString('en-US')} {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · {APP_VERSION}</span>
    </div>
  );
}

export const Sub = ({ children }: { children: ReactNode }) => <p className="sub">{children}</p>;

/** Runs `fn` after paint (setTimeout 0) whenever `key` changes, so a slow report does not freeze typing. */
export function useDeferred<T>(fn: () => T, key: unknown): { value: T | null; busy: boolean; error: string | null } {
  const [state, setState] = useState<{ key: unknown; value: T | null; error: string | null }>({ key: undefined, value: null, error: null });
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      try { const value = fn(); if (live) setState({ key, value, error: null }); } catch (e) { if (live) setState({ key, value: null, error: e instanceof Error ? e.message : String(e) }); }
    }, 30);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { value: state.value, busy: state.key !== key, error: state.error };
}

export const Busy = ({ what }: { what: string }) => <div className="ph" data-busy="1" style={{ height: 300 }}><div>Computing {what}…</div></div>;
